import * as React from "react";

// NPM
import { animated, useTrail, config, interpolate } from "react-spring";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";

// Utility
import Utility from "../domain/classes/Utility";

// Assets

// Components
import StyledSectionContainer from "../components/common/SectionContainer.Component";
import { AnimatedAppointmentTypeGroup } from "../components/appointmentTypeSelection/AppointmentTypeGroup.Component";
import { AnimatedAppointmentTypeSearchBar } from "../components/appointmentTypeSelection/AppointmentTypeSearchBar.Component";
import { AnimatedLoader } from "../components/common/Loader.Component";
import ErrorDisplay from "../components/common/ErrorDisplay.Component";
import { AnimatedAppointmentTypeItem } from "../components/appointmentTypeSelection/AppointmentTypeItem.Component";

// Classes
import AppointmentType from "../domain/classes/AppointmentType";

// Errors
import { UnableToGetAppointmentTypeError } from "../domain/classes/errors/UnableToGetAppointmentTypeError";

// Interfaces
import IAppointmentType from "../domain/interfaces/IAppointmentType";
import IRequestResponse, {
	InitialRquestResponseState,
} from "../domain/interfaces/IRequestResponse";

// Contexts
import { useApplicationManager } from "../contexts/ApplicationManager.Context";
import { WARNING_COLOURED } from "../domain/utility/config";
import IAnimatable from "../domain/interfaces/IAnimatable";
import { AxiosError } from "axios";

export const styles = (theme: Theme) => createStyles({ root: {}, stepper: {} });

export interface SelectAppointmentTypeSectionProps extends IAnimatable {}

const SelectAppointmentTypeSection = React.forwardRef<
	HTMLDivElement,
	SelectAppointmentTypeSectionProps & WithStyles<typeof styles>
>(({ classes, hasAnimation }, ref) => {
	const { location } = useApplicationManager();

	// States
	const [categories, setCategories] = React.useState<string[]>([]);
	const [expanded, setExpanded] = React.useState<string | false>("General");
	const [searchTerm, setSearchTerm] = React.useState<string>("");
	const [appointmentTypeList, setAppointmentTypeList] = React.useState<
		IAppointmentType[]
	>([]);
	const [requestResponse, setRequestResponse] =
		React.useState<IRequestResponse>(InitialRquestResponseState);

	// Callbacks
	const getAllAppointmentTypes = React.useCallback(async (orgId: number) => {
		setRequestResponse((prevState: IRequestResponse) => ({
			...prevState,
			loading: true,
		}));

		try {
			const response = await AppointmentType.getAll(orgId);

			if (response.status !== 200)
				throw new UnableToGetAppointmentTypeError();

			setAppointmentTypeList(
				response.data.sort((a: IAppointmentType, b: IAppointmentType) =>
					Utility.sortByAttribute(a, b, "displayOrder")
				)
			);
		} catch (error) {
			setRequestResponse((prevState: IRequestResponse) => ({
				...prevState,
				error: (error as AxiosError).message,
			}));
		}
		setRequestResponse((prevState: IRequestResponse) => ({
			...prevState,
			loading: false,
		}));
	}, []);

	// Effects
	React.useEffect(() => {
		const queryParam = new URLSearchParams(window.location.search).get(
			"search"
		);

		setSearchTerm(queryParam ? queryParam : "");
	}, []);

	// React.useEffect(() => {
	// Utility.debug(searchTerm);
	// }, [searchTerm]);

	React.useEffect(() => {
		const getCategories = () => {
			const categoryList: string[] =
				AppointmentType.extractCategories(appointmentTypeList);
			setCategories([...categoryList]);
		};
		getCategories();
	}, [appointmentTypeList]);

	React.useEffect(()=>{
		console.log('location changed');
		console.log(location);
		console.trace()
	},[location])

	React.useEffect(() => {
		if (location) getAllAppointmentTypes(location?.organisationId);
	}, [getAllAppointmentTypes, location]);

	// Handlers
	const handleAccordionChange =
		(panel: string | false) =>
		(event: React.ChangeEvent<{}>, isExpanded: boolean) => {
			setExpanded(isExpanded ? panel : false);
		};

	const handleSearchTermChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setSearchTerm(event.target.value);
	};

	const handleClearSearchTerm = () => {
		setSearchTerm("");
	};

	//Springs
	const trail = useTrail(categories.length, {
		config: config.wobbly,
		opacity: 1,
		x: 0,
		from: {
			opacity: 0,
			x: -50,
		},
	});

	return (
		<StyledSectionContainer
			ref={ref}
			className={classes.root}
			maxWidth="lg"
		>
			<AnimatedAppointmentTypeSearchBar
				onSearchTermChange={handleSearchTermChange}
				onClearSearchTerm={handleClearSearchTerm}
				searchTerm={searchTerm}
			/>
			{requestResponse.error && (
				<ErrorDisplay
					message={requestResponse.error}
					src={WARNING_COLOURED}
				/>
			)}
			{!requestResponse.loading && appointmentTypeList.length === 0 && (
				<ErrorDisplay
					message="Unable to get appointment types."
					src={WARNING_COLOURED}
				/>
			)}
			{requestResponse.loading && <AnimatedLoader />}

			{searchTerm && (
				<Grid container spacing={2}>
					{appointmentTypeList
						.filter((item: IAppointmentType) =>
							AppointmentType.search(item, searchTerm)
						)
						.map((item: IAppointmentType) => (
							<AnimatedAppointmentTypeItem
								clickable
								showImg
								apptType={item}
								key={item.appointmentTypeName}
							/>
						))}
				</Grid>
			)}
			{categories.length > 0 && !searchTerm && (
				<React.Fragment>
					{hasAnimation
						? trail.map(({ x, opacity, ...rest }, index) => {
								const currentCategory = categories[index];

								const apptTypes: IAppointmentType[] =
									appointmentTypeList.filter(
										(item: IAppointmentType) =>
											AppointmentType.groupByCategory(
												item,
												currentCategory
											)
									);

								return (
									<AnimatedAppointmentTypeGroup
										expanded={expanded}
										key={currentCategory}
										style={{
											opacity,
											transform: interpolate(
												[x],
												(x) => `translateX(${x}px)`
											),
											...rest,
										}}
										apptTypes={apptTypes}
										category={currentCategory}
										onAccordionChange={
											handleAccordionChange
										}
									/>
								);
						  })
						: categories.map((category: string) => {
								const apptTypes: IAppointmentType[] =
									appointmentTypeList.filter(
										(item: IAppointmentType) =>
											AppointmentType.groupByCategory(
												item,
												category
											)
									);

								return (
									<AnimatedAppointmentTypeGroup
										expanded={expanded}
										key={category}
										apptTypes={apptTypes}
										category={category}
										onAccordionChange={
											handleAccordionChange
										}
									/>
								);
						  })}
				</React.Fragment>
			)}
		</StyledSectionContainer>
	);
});

const StyledSelectAppointmentTypeSection = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("SelectAppointmentTypeSection"),
})(SelectAppointmentTypeSection);

export default StyledSelectAppointmentTypeSection;

export const AnimatedSelectAppointmentTypeSection = animated(
	StyledSelectAppointmentTypeSection
);
